import _ from "lodash";
import { Duration } from "luxon";
import { CyclicArray, sameMonths } from "../helpers";
import { Compactor } from "./compactRules";
import formatContiguousValues from "../parsers/formatContiguousValues";
import { MONTHS_OF_YEAR } from "../../constants";

/**
 * @typedef {import('./compactRules').TimedGeofenceRule} TimedGeofenceRule
 * @typedef {import('./compactRules').Schedule} Schedule
 */

const DAYS_OF_WEEK = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/**
 * @param {Array<[string, string]> | null} pairs
 * @returns {boolean}
 */
function areWeekdayPairsContiguous(pairs) {
  return pairs
    .map(([startDay, endDay]) => [DAYS_OF_WEEK.indexOf(startDay), DAYS_OF_WEEK.indexOf(endDay)])
    .every(([startDay, endDay]) => (startDay === 6 && endDay === 0) || endDay - startDay === 1);
}

/**
 * @param {TimedGeofenceRule} startRule
 * @param {TimedGeofenceRule} endRule
 *
 * @returns {Array<[string, string]> | null}
 */
function getContiguousWeekdayPairs(startRule, endRule) {
  // sort start and end days according to their order in the week
  const startDays = _.sortBy(startRule.week_days, (day) => DAYS_OF_WEEK.indexOf(day));
  const endDays = new CyclicArray(
    ..._.sortBy(endRule.week_days, (day) => DAYS_OF_WEEK.indexOf(day)),
  );

  let pairs = _.zip(startDays, endDays);

  if (areWeekdayPairsContiguous(pairs)) return pairs;

  endDays.rotate();
  pairs = _.zip(startDays, endDays);

  if (areWeekdayPairsContiguous(pairs)) return pairs;

  return null;
}

/**
 * @param {TimedGeofenceRule} startRule
 * @param {TimedGeofenceRule} endRule
 *
 * @returns {boolean}
 */
export function areDaysOfWeekContiguous(startRule, endRule) {
  return getContiguousWeekdayPairs(startRule, endRule) !== null;
}

/**
 * @param {TimedGeofenceRule} startRule
 * @param {TimedGeofenceRule} endRule
 *
 * @returns {boolean}
 */
export function areDaysOfMonthContiguous(startRule, endRule) {
  if (startRule.month_days.length !== endRule.month_days.length) return false;

  const startDays = startRule.month_days;
  const endDays = endRule.month_days;

  return _.zip(startDays, endDays).every(([startDay, endDay]) => endDay - startDay === 1);
}

/**
 * @param {TimedGeofenceRule} rule
 * @returns {boolean}
 */
const startsAtMidnight = (rule) => rule.startTime === "00:00";

/**
 * @param {TimedGeofenceRule} rule
 * @returns {boolean}
 */
const endsAtMidnight = (rule) => rule.endTime === "23:59";

/**
 *
 * @param {TimedGeofenceRule} rule time in hh:mm format
 *
 * @returns number
 */
function getTimeDiff({ startTime, endTime }) {
  const [startHour, startMin] = startTime.split(":").map(Number);
  const [endHour, endMin] = endTime.split(":").map(Number);

  let diffInHours = endHour - startHour;

  let diffInMins = endMin - startMin;
  if (diffInMins < 0) {
    diffInMins += 60;
    diffInHours -= 1;
  }

  let diffInDays = 0;
  if (diffInHours >= 24) {
    diffInHours -= 24;
    diffInDays = 1;
  }

  return diffInDays * 24 * 60 + diffInHours * 60 + diffInMins;
}

const overnightCompactor = new Compactor({
  /**
   * @param {TimedGeofenceRule} r1
   * @param {TimedGeofenceRule} r2
   * @returns {boolean}
   */
  isCompactableFn: (r1, r2) => {
    let startRule;
    let endRule;

    if (endsAtMidnight(r1) && startsAtMidnight(r2)) {
      startRule = r1;
      endRule = r2;
    } else if (endsAtMidnight(r2) && startsAtMidnight(r1)) {
      startRule = r2;
      endRule = r1;
    } else return false;

    return (
      sameMonths(r1, r2) &&
      areDaysOfMonthContiguous(startRule, endRule) &&
      areDaysOfWeekContiguous(startRule, endRule)
    );
  },

  /**
   * @param {TimedGeofenceRule} r1
   * @param {TimedGeofenceRule} r2
   * @returns {TimedGeofenceRule}
   */
  mergeRulesFn: (r1, r2) => {
    let startRule;
    let endRule;

    if (endsAtMidnight(r1) && startsAtMidnight(r2)) {
      startRule = r1;
      endRule = r2;
    } else if (endsAtMidnight(r2) && startsAtMidnight(r1)) {
      startRule = r2;
      endRule = r1;
    }

    return {
      ruleCount: (startRule.ruleCount || 1) + (endRule.ruleCount || 1),
      startTime: startRule.startTime,
      endTime: endRule.endTime,
      week_days: startRule.week_days,
      month_days: startRule.month_days,
      months: startRule.months,
      duration: startRule.duration + endRule.duration + 1,
    };
  },
});

/**
 * @param {Schedule} schedule an object containing the rule groups.
 * @returns {Schedule} an object containing compacted Rules.
 */
export default (schedule) => {
  let rules = schedule.rules;

  if (rules)
    rules = schedule.rules.map((rule) => ({
      ...rule,
      duration: getTimeDiff(rule),
    }));

  const compactedSchedule = overnightCompactor.compact({ rules });

  if (compactedSchedule.rules)
    compactedSchedule.rules = compactedSchedule.rules.map((rule) => {
      const duration = Duration.fromObject({
        days: 0,
        hours: 0,
        minutes: rule.duration,
      })
        .normalize()
        .toObject();

      const diffInDays = duration.days;
      const diffInHours = duration.hours;
      const diffInMins = duration.minutes;

      const dayOutput = diffInDays > 0 ? `${diffInDays}d` : "";
      const hourOutput = diffInHours > 0 ? `${diffInHours}h` : "";
      const minOutput = diffInMins > 0 ? `${diffInMins}m` : "";

      rule.months = formatContiguousValues(rule.months, MONTHS_OF_YEAR);
      rule.month_days = formatContiguousValues(rule.month_days);

      return {
        ...rule,
        duration: _.compact([dayOutput, hourOutput, minOutput]).join(" "),
      };
    });

  return compactedSchedule;
};
